import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Input,
  Spinner,
  Stack,
  HStack,
  Badge,
  Textarea,
} from '@chakra-ui/react';
import { Table } from '@chakra-ui/react';
import { Alert } from '@chakra-ui/react';
import { api, Reseller } from '../api/api';
import { useConfirmation, useNotification } from '../context/NotificationContext';
import { FormControl, FormLabel, FormHelperText } from '../components/FormControls';
import { Panel, PanelBody, PanelHeader } from '../components/Panel';
import SimpleModal from '../components/SimpleModal';
import { EditIcon, PlusIcon, TrashIcon } from '../components/Icons';

const ResellersPage: React.FC = () => {
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [form, setForm] = useState<Partial<Reseller>>({});
  const [editId, setEditId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { confirm } = useConfirmation();
  const { notify } = useNotification();

  const load = () => {
    setLoading(true);
    api.resellers.list()
      .then(setResellers)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const resetForm = () => {
    setForm({});
    setEditId(null);
    setShowCreateForm(false);
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      notify({ type: 'error', message: 'Reseller name is required' });
      return;
    }

    try {
      if (editId) {
        await api.resellers.update(editId, form as Reseller);
        notify({ type: 'success', message: 'Reseller updated successfully' });
      } else {
        await api.resellers.create(form as Reseller);
        notify({ type: 'success', message: 'Reseller created successfully' });
      }
      resetForm();
      load();
    } catch (error: any) {
      notify({ type: 'error', message: error?.response?.data?.error || 'Failed to save reseller' });
    }
  };

  const handleEdit = (reseller: Reseller) => {
    setForm(reseller);
    setEditId(reseller.id);
    setShowCreateForm(true);
  };

  const handleDelete = (id: number) => {
    confirm({
      title: 'Delete Reseller',
      message: 'Are you sure you want to delete this reseller? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmColorScheme: 'red',
      onConfirm: async () => {
        try {
          await api.resellers.delete(id);
          notify({ type: 'success', message: 'Reseller deleted successfully' });
          load();
        } catch (error: any) {
          notify({ type: 'error', message: error?.response?.data?.error || 'Failed to delete reseller' });
        }
      }
    });
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">Resellers</Heading>
        <Button
          colorScheme="blue"
          onClick={() => setShowCreateForm(true)}
        >
          <PlusIcon />
          Add Reseller
        </Button>
      </HStack>

      {loading && <Spinner />}

      <Panel>
        <PanelHeader>
          <Heading size="md">Reseller List ({resellers.length})</Heading>
        </PanelHeader>
        <PanelBody>
          {resellers.length === 0 && !loading && (
            <Alert.Root status="info">
              <Alert.Content>
                <Alert.Description>No resellers found. Create one to get started.</Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}
          {resellers.length > 0 && (
            <Table.Root variant="outline" size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Contact Person</Table.ColumnHeader>
                  <Table.ColumnHeader>Email</Table.ColumnHeader>
                  <Table.ColumnHeader>Phone</Table.ColumnHeader>
                  <Table.ColumnHeader>Address</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {resellers.map(reseller => (
                  <Table.Row key={reseller.id}>
                    <Table.Cell>
                      <strong>{reseller.name}</strong>
                    </Table.Cell>
                    <Table.Cell>{reseller.contactPerson || '-'}</Table.Cell>
                    <Table.Cell>{reseller.email || '-'}</Table.Cell>
                    <Table.Cell>{reseller.phone || '-'}</Table.Cell>
                    <Table.Cell>{reseller.address || '-'}</Table.Cell>
                    <Table.Cell>
                      <Badge colorScheme={reseller.isActive ? 'green' : 'gray'}>
                        {reseller.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <Button
                          size="sm"
                          variant="outline"
                          colorPalette="blue"
                          onClick={() => handleEdit(reseller)}
                          title="Edit"
                        >
                          <EditIcon />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          colorPalette="red"
                          onClick={() => handleDelete(reseller.id)}
                          title="Delete"
                        >
                          <TrashIcon />
                        </Button>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </PanelBody>
      </Panel>

      <SimpleModal
        isOpen={showCreateForm}
        onClose={resetForm}
        title={editId ? 'Edit Reseller' : 'Create Reseller'}
        footer={
          <>
            <Button onClick={resetForm}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {editId ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Stack gap={4}>
          <FormControl isRequired>
            <FormLabel>Reseller Name</FormLabel>
            <Input
              value={form.name || ''}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Enter reseller name"
            />
            <FormHelperText>The official name of the reseller</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Contact Person</FormLabel>
            <Input
              value={form.contactPerson || ''}
              onChange={e => setForm({ ...form, contactPerson: e.target.value })}
              placeholder="Enter contact person name"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={form.email || ''}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="Enter email address"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Phone</FormLabel>
            <Input
              value={form.phone || ''}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              placeholder="Enter phone number"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Address</FormLabel>
            <Textarea
              value={form.address || ''}
              onChange={e => setForm({ ...form, address: e.target.value })}
              placeholder="Enter address"
              rows={3}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Status</FormLabel>
            <HStack>
              <Button
                size="sm"
                colorScheme={form.isActive ?? true ? 'green' : 'gray'}
                variant={form.isActive ?? true ? 'solid' : 'outline'}
                onClick={() => setForm({ ...form, isActive: true })}
              >
                Active
              </Button>
              <Button
                size="sm"
                colorScheme={!(form.isActive ?? true) ? 'red' : 'gray'}
                variant={!(form.isActive ?? true) ? 'solid' : 'outline'}
                onClick={() => setForm({ ...form, isActive: false })}
              >
                Inactive
              </Button>
            </HStack>
            <FormHelperText>Inactive resellers won't be shown in system forms</FormHelperText>
          </FormControl>
        </Stack>
      </SimpleModal>
    </Box>
  );
};

export default ResellersPage;
